import { Box, Card, Grid2, Paper, Slide, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { PintIcon } from '../../assets/custom_icons/customIcons';
import type { IIsPdf } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';
import { AvaniNumberSpin } from '../DataDisplay';

interface AvaniSavedBeerCardProps extends IIsPdf {
  title?: string;
  titleNode?: ReactNode;
  beerSaved: number;
  co2?: boolean;
  water?: boolean;
  text?: string;
  textNode?: ReactNode;
  sx?: any;
}

const AvaniSavedBeerStyledCard = styled(Card)(
  () => `
  border-radius: 16px;
  padding: 16px 0;

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0 0 0 16px;
    line-height: 1;
    padding-bottom: 24px;
    display: block;
    width: 100%;
    float: left;
  }

  &.AvaniBeerCard {
    position: relative;
    min-height: 248px;

    .AvaniAnimatedBackground,
    .AvaniCardContent {
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      left: auto;
      bottom: 0;
      z-index: 1;
    }

    .AvaniCardContent .MuiTypography-root.MuiTypography-h3 {
      float: left;
      width: 100%;
      margin-top: 56px;
      padding-bottom: 4px;
    }

    .AvaniAnimatedBackground {
      top: auto;
      bottom: -29px;
      z-index: 0;
    }
  }
`
);

export const AvaniSavedBeerCard: FC<AvaniSavedBeerCardProps> = ({
  title,
  titleNode,
  beerSaved,
  text,
  textNode,
  sx,
  water,
  co2,
  isPDF = false
}) => {
  const theme = isPDF ? AvaniLightTheme : useTheme();
  const CardEl = useRef<HTMLDivElement>(null);

  const [svgHeight, setSvgHeight] = useState(CardEl?.current ? CardEl.current.clientHeight - 39 : 188);
  const [svgWidth, setSvgWidth] = useState(CardEl?.current ? CardEl.current.clientWidth : 251);
  const clickTriggerRef = useRef(0);
  const [clickTrigger, setClickTrigger] = useState(0);

  const handleClickTrigger = useCallback(() => {
    clickTriggerRef.current += 1;
    setClickTrigger(clickTriggerRef.current);
  }, []);

  useEffect(() => {
    const onResize = () => {
      setSvgHeight(CardEl?.current ? CardEl.current.clientHeight - 39 : 188);
      setSvgWidth(CardEl?.current ? CardEl.current.clientWidth : Math.round(svgHeight * 1.34));
    };

    onResize();

    window.addEventListener('resize', onResize);

    if (!CardEl.current) {
      return window.removeEventListener('resize', onResize);
    } else {
      const resizeObserver = new ResizeObserver(() => {
        onResize();
      });

      resizeObserver.observe(CardEl.current);

      return () => {
        window.removeEventListener('resize', onResize);
        resizeObserver.disconnect();
      };
    }
    //todo:v2-changes
  }, []);

  const hasPintsIcon = !water && !co2;

  return (
    <AvaniSavedBeerStyledCard
      raised={true}
      className={`AvaniBeerCard`}
      ref={CardEl}
      sx={{
        height: '100%',
        backgroundColor: theme.colors.avani.lightTurc,
        ['.MuiPieArc-root']: { stroke: 'none' },
        ['.SavedBeerSpinner']: {
          float: 'right',
          clear: 'both',
          textAlign: 'right',
          marginTop: 0,
          [theme.breakpoints.down('lg')]: {
            paddingRight: '12px'
          }
        },
        ...sx
      }}
    >
      <Box className={'AvaniAnimatedBackground'}>
        <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={{ enter: 880, exit: 2000 }}>
          <Paper elevation={0} sx={{ padding: 0, backgroundColor: 'transparent', height: '100%' }} component={'div'}>
            <svg
              width={svgWidth}
              height={svgHeight}
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d={`M0.00021413 83C0.000166991 7.99995 ${svgWidth} 84.9999 ${svgWidth} 13.9999C${svgWidth} -45.2168 ${svgWidth} 99.6879 ${svgWidth} 188.084V195H0.000222214L0.000122078 194C0.000133093 182.448 0.000225314 16.206 0.00021413 83Z`}
                fill={water ? theme.colors.avani.aWater : co2 ? theme.colors.avani.aCO2 : theme.colors.avani.aYellow}
              />
            </svg>
          </Paper>
        </Slide>
      </Box>
      <Box className={'AvaniCardContent'} sx={{ position: 'relative', zIndex: 2 }}>
        <Grid2 container direction={'row'} columns={12} sx={{ gap: 0 }} justifyContent={'end'}>
          <Grid2 size={{ xs: hasPintsIcon ? 10 : 12 }} paddingRight={!hasPintsIcon ? '24px' : undefined}>
            <div className={'SavedBeerSpinner'}>
              <AvaniNumberSpin
                numberValue={beerSaved}
                fSize={50}
                isPDF={isPDF}
                clickTrigger={clickTrigger}
                onClickTrigger={handleClickTrigger}
              />
            </div>
            <Typography variant="h3" sx={{ color: theme.colors.avani.teal, textAlign: 'left' }}>
              {title ?? titleNode}
            </Typography>
          </Grid2>
          {hasPintsIcon && (
            <Grid2 size={{ xs: 2 }} paddingTop={2} paddingRight={'24px'}>
              {PintIcon({ width: 42, height: 42, isPDF })}
            </Grid2>
          )}
        </Grid2>
        <Typography variant="body2" fontSize={14} sx={{ padding: 2, paddingY: 0, pt: 1, color: theme.colors.primary }}>
          {text ?? textNode}
        </Typography>
      </Box>
    </AvaniSavedBeerStyledCard>
  );
};
